import React, { useState } from 'react';
import { Sun, Snowflake, CloudSun, Fuel, Droplets, Volume2, Car, Truck, Mountain, ShieldCheck, Disc3 } from 'lucide-react';
import { i18n } from '../../services/i18n';
import EuTyreLabelDialog from './EuTyreLabelDialog';
import { FileText } from 'lucide-react';

const tr = (k, v) => i18n.t(k, v);

const SEASON = { ete: { label: 'tyre.summer', Icon: Sun, cls: 'bg-amber-100 text-amber-800 border-amber-200' }, hiver: { label: 'tyre.winter', Icon: Snowflake, cls: 'bg-sky-100 text-sky-800 border-sky-200' }, '4saisons': { label: 'tyre.allSeason', Icon: CloudSun, cls: 'bg-emerald-100 text-emerald-800 border-emerald-200' } };
const VT = { voiture: ['tyre.car', Car], suv: ['SUV / 4x4', Mountain], utilitaire: ['tyre.van', Truck] };
const GRADE = { A: 'bg-green-600', B: 'bg-lime-500', C: 'bg-yellow-400', D: 'bg-orange-500', E: 'bg-red-600' };
const NOISE_CLASS = (db) => (db == null ? null : db <= 68 ? 'A' : db <= 71 ? 'B' : 'C');

const Badge = ({ cls, Icon, children, testId }) => <span data-testid={testId} className={`inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-xs font-semibold ${cls}`}>{Icon && <Icon size={13} />}{children}</span>;

const Grade = ({ label, Icon, value, hint, testId }) => value ? (
  <div className="flex items-center gap-2" data-testid={testId} title={hint}>
    <span className={`w-9 h-9 rounded-md ${GRADE[String(value).toUpperCase()] || 'bg-gray-400'} text-white font-black text-lg flex items-center justify-center shadow-inner`}>{String(value).toUpperCase()}</span>
    <span className="text-xs text-gray-600 leading-tight flex items-center gap-1"><Icon size={13} className="text-gray-400" /> {label}</span>
  </div>
) : null;

export const tyreSpecRows = (t) => [
  [tr('tyre.size'), `${t.width}/${t.height} R${t.diameter}`],
  ...(t.load_index || t.speed_index ? [[tr('tyre.indexes'), `${t.load_index || '—'} ${t.speed_index || ''}`.trim()]] : []),
  [tr('tyre.season'), SEASON[t.season] ? tr(SEASON[t.season].label) : '—'],
  [tr('tyre.vehicleType'), tr(VT[t.vehicle_type]?.[0] || 'tyre.car')],
  ...(t.label ? [[tr('tyre.euLabel'), `${tr('tyre.fuel')} ${t.label.fuel || '—'} · ${tr('tyre.wet')} ${t.label.wet || '—'} · ${t.label.noise_db ? `${t.label.noise_db} dB` : '—'}`]] : []),
  ...(t.xl || t.runflat || t.snowflake || t.ms ? [[tr('tyre.specifics'), [t.xl && tr('tyre.xlSpec'), t.runflat && 'Runflat', t.snowflake && '3PMSF', t.ms && 'M+S'].filter(Boolean).join(', ')]] : []),
];

export const wheelSpecRows = (w) => [
  [tr('tyre.size'), `${w.width}J x ${w.diameter}`],
  [tr('tyre.pcd'), w.holes && w.pcd ? `${w.holes}x${w.pcd}` : '—'],
  ...(w.et != null ? [[tr('tyre.offset'), `ET${w.et}`]] : []),
  ...(w.center_bore ? [[tr('tyre.centerBore'), `${String(w.center_bore).replace('.', ',')} mm`]] : []),
  [tr('tyre.material'), w.material === 'acier' ? tr('tyre.steel') : tr('tyre.aluminium')],
  ...(w.color ? [[tr('tyre.finish'), w.color]] : []),
  [tr('tyre.vehicleType'), tr(VT[w.vehicle_type]?.[0] || 'tyre.car')],
];

const EuLabelButton = ({ product }) => {
  const [open, setOpen] = useState(false);
  return (
    <>
      <button type="button" onClick={() => setOpen(true)} className="inline-flex items-center gap-1.5 text-xs font-medium text-[#1e3a5f] border border-[#1e3a5f]/30 hover:bg-blue-50 rounded-md px-2.5 py-1.5" data-testid="tyre-eu-label-open">
        <FileText size={13} /> {i18n.t('tyre.euLabelView')}
      </button>
      {open && <EuTyreLabelDialog product={product} open={open} onOpenChange={setOpen} />}
    </>
  );
};

const TyreProductBadges = ({ product }) => {
  const t = product.tyre;
  const w = product.wheel;
  if (!t && !w) return null;
  if (w) {
    const [vt, VIcon] = VT[w.vehicle_type] || VT.voiture;
    return (
      <div className="mt-3 flex flex-wrap gap-2" data-testid="wheel-badges">
        <Badge cls="bg-slate-100 text-slate-800 border-slate-200" Icon={Disc3} testId="wheel-badge-material">{w.material === 'acier' ? tr('tyre.steelWheel') : tr('tyre.aluWheel')}</Badge>
        <Badge cls="bg-gray-100 text-gray-700 border-gray-200" Icon={VIcon} testId="wheel-badge-vehicle">{tr(vt)}</Badge>
        {w.holes && w.pcd && <Badge cls="bg-sky-50 text-sky-800 border-sky-200" testId="wheel-badge-pcd">{tr('tyre.pcd')} {w.holes}x{w.pcd}</Badge>}
      </div>
    );
  }
  const s = SEASON[t.season] || SEASON.ete;
  const [vt, VIcon] = VT[t.vehicle_type] || VT.voiture;
  const noise = t.label?.noise_db;
  return (
    <div className="mt-3 space-y-3" data-testid="tyre-badges">
      <div className="flex flex-wrap gap-2">
        <Badge cls={s.cls} Icon={s.Icon} testId="tyre-badge-season">{tr(s.label)}</Badge>
        <Badge cls="bg-gray-100 text-gray-700 border-gray-200" Icon={VIcon} testId="tyre-badge-vehicle">{tr(vt)}</Badge>
        {t.snowflake && <Badge cls="bg-blue-50 text-blue-800 border-blue-200" Icon={Snowflake} testId="tyre-badge-3pmsf">{tr('tyre.snowflake')}</Badge>}
        {t.ms && !t.snowflake && <Badge cls="bg-blue-50 text-blue-800 border-blue-200" testId="tyre-badge-ms">M+S</Badge>}
        {t.xl && <Badge cls="bg-purple-50 text-purple-800 border-purple-200" Icon={ShieldCheck} testId="tyre-badge-xl">{tr('tyre.xl')}</Badge>}
        {t.runflat && <Badge cls="bg-rose-50 text-rose-800 border-rose-200" testId="tyre-badge-runflat">Runflat</Badge>}
      </div>
      {t.label && (t.label.fuel || t.label.wet || noise) && (
        <div className="inline-flex flex-wrap items-center gap-x-5 gap-y-3 rounded-lg border border-gray-200 bg-gray-50 px-4 py-2.5" data-testid="tyre-eu-label">
          <Grade label={tr('tyre.fuel')} Icon={Fuel} value={t.label.fuel} hint={tr('tyre.fuelHint')} testId="tyre-eu-fuel" />
          <Grade label={tr('tyre.wetGrip')} Icon={Droplets} value={t.label.wet} hint={tr('tyre.wetHint')} testId="tyre-eu-wet" />
          {noise != null && (
            <div className="flex items-center gap-2" data-testid="tyre-eu-noise" title={tr('tyre.noiseTitle')}>
              <span className={`w-9 h-9 rounded-md ${GRADE[NOISE_CLASS(noise)] || 'bg-gray-400'} text-white font-black text-sm flex items-center justify-center shadow-inner`}>{noise}<span className="text-[8px] ml-0.5">dB</span></span>
              <span className="text-xs text-gray-600 leading-tight flex items-center gap-1"><Volume2 size={13} className="text-gray-400" /> {tr('tyre.noiseClass', { c: NOISE_CLASS(noise) })}</span>
            </div>
          )}
          <EuLabelButton product={product} />
        </div>
      )}
    </div>
  );
};

export default TyreProductBadges;
